/**
 * Fonte de verdade de uma partida, vista pela interface.
 *
 * Duas implementacoes com a mesma cara: a de demonstracao, que roda inteira
 * no navegador, e a ao vivo, que fala WebSocket com o motor autoritativo. A
 * tela do tabuleiro nao sabe qual das duas esta usando.
 *
 * Fica explicito aqui: no modo demonstracao o navegador arbitra a propria
 * partida. Isso e aceitavel porque nao ha dinheiro em jogo. Em partida de
 * verdade, quem arbitra e sempre o servidor.
 */
import { rulesFor } from '@engine/games/index.ts';
import type { GameRules, Move, Outcome, Seat } from '@engine/games/types.ts';

import { chooseMove, createRandom, thinkDelayMs } from './bot.ts';

export type GameType = 'CHESS' | 'CHECKERS' | 'TIC_TAC_TOE';

export interface MatchSnapshot {
  readonly gameType: GameType;
  /** Estado serializado, no formato de cada jogo. */
  readonly state: string;
  readonly turn: Seat;
  readonly mySeat: Seat;
  readonly legalMoves: readonly Move[];
  readonly remainingMs: readonly [number, number];
  readonly outcome: Outcome | null;
  readonly opponentName: string;
  readonly lastMove: Move | null;
  /** Mensagem de recusa a mostrar ao jogador, se houver. */
  readonly error: string | null;
}

export interface MatchAdapter {
  subscribe(listener: (snapshot: MatchSnapshot) => void): () => void;
  play(move: Move): void;
  resign(): void;
  dispose(): void;
}

export interface DemoMatchOptions {
  readonly gameType: GameType;
  readonly opponentName: string;
  readonly seed: number;
  readonly initialMs?: number;
  readonly incrementMs?: number;
  readonly skill?: number;
}

/**
 * Partida de demonstracao: o jogador ocupa o assento 0 e o bot o 1.
 * O relogio corre de verdade, com incremento, para que a interface do tempo
 * seja exercitada do mesmo jeito que numa partida real.
 */
export function createDemoMatch(options: DemoMatchOptions): MatchAdapter {
  const rules = rulesFor(options.gameType) as GameRules<unknown>;
  const random = createRandom(options.seed);
  const initialMs = options.initialMs ?? 300_000;
  const incrementMs = options.incrementMs ?? 3_000;

  let state: unknown = rules.initial();
  let remaining: [number, number] = [initialMs, initialMs];
  let running: Seat = 0;
  let lastTick = Date.now();
  let outcome: Outcome | null = null;
  let lastMove: Move | null = null;
  let error: string | null = null;

  const listeners = new Set<(snapshot: MatchSnapshot) => void>();
  let botTimer: ReturnType<typeof setTimeout> | null = null;
  let clockTimer: ReturnType<typeof setInterval> | null = null;

  function snapshot(): MatchSnapshot {
    return {
      gameType: options.gameType,
      state: rules.serialize(state),
      turn: rules.turn(state),
      mySeat: 0,
      legalMoves: outcome ? [] : rules.legalMoves(state),
      remainingMs: [remaining[0], remaining[1]],
      outcome,
      opponentName: options.opponentName,
      lastMove,
      error,
    };
  }

  function emit(): void {
    const snap = snapshot();
    for (const listener of listeners) listener(snap);
  }

  function finish(result: Outcome): void {
    outcome = result;
    if (botTimer) clearTimeout(botTimer);
    if (clockTimer) clearInterval(clockTimer);
    botTimer = null;
    clockTimer = null;
    emit();
  }

  /** Desconta o tempo de quem esta na vez e derruba quem estoura. */
  function tick(): void {
    if (outcome) return;
    const agora = Date.now();
    const decorrido = agora - lastTick;
    lastTick = agora;

    remaining[running] = Math.max(0, remaining[running] - decorrido);

    if (remaining[running] === 0) {
      finish({
        kind: 'WIN',
        seat: running === 0 ? 1 : 0,
        reason: 'TEMPO_ESGOTADO',
      });
      return;
    }
    emit();
  }

  function switchClock(): void {
    const agora = Date.now();
    remaining[running] = Math.max(0, remaining[running] - (agora - lastTick));
    if (remaining[running] > 0) remaining[running] += incrementMs;
    lastTick = agora;
    running = running === 0 ? 1 : 0;
  }

  function afterMove(move: Move): void {
    lastMove = move;
    switchClock();

    const resultado = rules.outcome(state);
    if (resultado) {
      finish(resultado);
      return;
    }
    emit();
    if (rules.turn(state) === 1) scheduleBot();
  }

  function scheduleBot(): void {
    if (botTimer) clearTimeout(botTimer);
    botTimer = setTimeout(() => {
      botTimer = null;
      if (outcome) return;
      const move = chooseMove(rules, state, { random, ...(options.skill !== undefined ? { skill: options.skill } : {}) });
      if (!move) {
        const resultado = rules.outcome(state);
        if (resultado) finish(resultado);
        return;
      }
      state = rules.apply(state, move);
      afterMove(move);
    }, thinkDelayMs(random));
  }

  clockTimer = setInterval(tick, 200);

  return {
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },

    play(move) {
      if (outcome) return;
      if (rules.turn(state) !== 0) {
        error = 'Não é a sua vez.';
        emit();
        return;
      }
      try {
        state = rules.apply(state, move);
        error = null;
      } catch (caught) {
        error = (caught as Error).message.replace(/^lance ilegal [^:]+: /, 'Lance inválido: ');
        emit();
        return;
      }
      afterMove(move);
    },

    resign() {
      if (!outcome) finish({ kind: 'WIN', seat: 1, reason: 'DESISTENCIA' });
    },

    dispose() {
      if (botTimer) clearTimeout(botTimer);
      if (clockTimer) clearInterval(clockTimer);
      listeners.clear();
    },
  };
}
